<template>
  <div ref="container" class="fill-height d-flex flex-column container-container">
    <material-list-view
      class="flex-grow-1"
      ref="materialListView"
      :background-color="backgroundColor"
      :items="materials"
      :total="materialsTotal"
      :card-bottom-size="cardBottomSize"
      :item-height="rowHeight"
      :item-height-offset.sync="itemHeightOffset"
      :items-per-row="itemsPerRow"
      :loading="loading"
      :author.sync="authorV"
      :license.sync="licenseV"
      :active-sort.sync="activeSort"
      :hide-filters="hideFilters"
      :selectable="selectable"
      :selected.sync="selectedV"
      :show-create-button="showCreateButton"
      :show-unpublished-value="showUnpublishedValue"
      :show-unpublished-hidden="showUnpublishedHidden"
      :no-gutters="noGutters"
      :hide-refresh="hideRefresh || connectToGlobalSettings"
      :hide-zoom="connectToGlobalSettings"
      :connect-to-global-settings="connectToGlobalSettings"
      @scroll:last="fetchMaterialsNext"
      @click:open="openMaterialShowDialog($event.id)"
      @click:refresh="refresh"
      @click:new-item="openDialogCreateMaterial"
      @update:author="$emit('update:author', $event)"
      @update:license="$emit('update:license', $event)"
    >
      <template #actions-left>
        <slot name="actions-left"/>
      </template>

      <template #actions>
        <slot name="actions"/>
      </template>

      <template #prepend>
        <slot name="prepend"/>
      </template>

      <template #item-hover="{ hover, item }">
        <slot name="item-hover" :hover="hover" :item="item"></slot>
      </template>
    </material-list-view>

    <material-dialog-show
      v-model="showMaterial"
      :loading="loadingMaterial"
      :item="shownItem"
      :show-actions="showViewActions"
      :show-comments="showViewComments"
      @click:tag="onClickTag"
      @click:author="onClickAuthor"
      @click:license="onClickLicense"
      @click:edit="openMaterialEditDialog"
      @input="onShowDialogInput"
      :prevBtnVisible="!!prevMaterialId"
      :nextBtnVisible="!!nextMaterialId"
    />

    <material-dialog-edit
      ref="editDialog"
      v-if="$can('update', $enums.Entity.MATERIAL) && showEditDialog"
      v-model="showEditDialog"
      :item="editedItem"
      @input="onEditDialogInput"
      @success:save="onSavedMaterial"
      @success:delete="onDeletedMaterialDialog"
    />

    <material-dialog-create
      v-if="$can('create', $enums.Entity.MATERIAL)"
      v-model="showDialogCreateMaterial"
      @success:create="onCreateMaterialDialog"
    />

  </div>
</template>

<script>
  import MaterialListView from '@/components/Materials/MaterialListView';

  import requestsUtils from '@/utils/requests';
  import arraysUtils from '@/utils/arrays';
  import objectsUtils from '@/utils/objects';

  const MaterialDialogShow = () => import ('./Dialogs/MaterialDialogShow');
  const MaterialDialogEdit = () => import ('./Dialogs/MaterialDialogEdit');
  const MaterialDialogCreate = () => import ('./Dialogs/MaterialDialogCreate');


  export default {
    name: 'MaterialListContainer',

    components: {
      MaterialListView,
      MaterialDialogShow,
      MaterialDialogEdit,
      MaterialDialogCreate,
    },

    props: {
      backgroundColor: {
        type: String,
      },
      filter: {
        type: Object,
        default: undefined,
      },
      gutterHeight: {
        type: Number,
        default: 4,
      },
      shownItemId: [String, Number],
      itemHeightStartOffset: {
        type: Number,
        default: 0,
      },
      selectable: {
        type: Boolean,
        default: false,
      },
      selected: {
        type: Array,
        default: () => [],
      },
      getAllApiFunctionName: {
        type: String,
        default: 'getAll',
      },
      showCreateButton: {
        type: Boolean,
        default: false,
      },
      showUnpublishedValue: {
        type: Boolean,
        default: undefined,
      },
      showUnpublishedHidden: {
        type: Boolean,
        default: false,
      },
      showViewActions: {
        type: Boolean,
        default: false,
      },
      showViewComments: {
        type: Boolean,
        default: false,
      },
      noGutters: {
        type: Boolean,
        default: false,
      },
      author: {
        type: String,
        default: undefined,
      },
      hideRefresh: {
        type: Boolean,
        default: false,
      },
      hideFilters: {
        type: Boolean,
        default: false,
      },
      editMode: {
        type: Boolean,
        default: false,
      },
      connectToGlobalSettings: {
        type: Boolean,
        default: true,
      },
      ignoreMaterialsIds: {
        type: Array,
        default: () => [],
      },
    },

    data() {
      return {
        width: 0,
        widthTemp: 0,
        itemHeightOffset: this.itemHeightStartOffset,
        cardBottomSize: 50,

        materials: [],
        loading: true,
        loadingKey: 1,
        materialsNext: "",
        materialsTotal: 0,
        activeSort: undefined,
        selectedV: [],

        loadingMaterial: false,

        showMaterial: false,
        showCreateDialog: false,
        showEditDialog: false,
        showDialogCreateMaterial: false,

        shownItem: undefined,
        shownItemIdV: undefined,
        editedItem: {},

        authorV: undefined,
        licenseV: undefined,
        updateKey: 1,

        cancel: undefined,
        cancelNext: undefined,

        prevMaterialId: undefined,
        nextMaterialId: undefined,
      };
    },

    computed: {
      itemsPerRow() {
        const { width, materialCardWidth } = this;
        const SCROLLBAR_WIDTH = 20;
        const PADDING_LEFT = 4;
        let freeSpaceWidth = width - PADDING_LEFT - SCROLLBAR_WIDTH;
        let itemsPerRow = Math.floor(freeSpaceWidth / materialCardWidth);
        if (isNaN(itemsPerRow) || itemsPerRow < 1) {
          return 1;
        }
        return itemsPerRow;
      },

      rowHeightBase() {
        let heightVariants = {
          md: 250,
          lg: 326,
          xl: 344,
          default: 250,
        };
        return heightVariants[this.$vuetify.breakpoint.name] ?? heightVariants.default;
      },

      rowHeight() {
        return this.rowHeightBase + this.itemHeightOffset;
      },

      materialCardWidth() {
        return this.rowHeight - this.cardBottomSize;
      },

      filterOrdered() {
        let { activeSort, filter, authorV, licenseV, updateKey } = this;
        activeSort ??= [];
        let result = {
          ...filter,
          updateKey,
        };
        // if (!hideFilters) {
          result.author = authorV;
          result.license = licenseV;
          result.ordering = activeSort.map( ({ ordering, id }) => `${ordering}${id}`);
        // }
        return result;
      },
    },

    watch: {
      shownItemId: {
        immediate: true,
        handler(v) {
          if (v !== this.shownItemIdV) {
            if (v) {
              this.openMaterialShowDialog(v);
            } else {
              this.closeMaterialShowDialog();
            }
          }
        },
      },

      shownItemIdV: {
        handler(v) {
          this.$emit('update:shown-item-id', v);
        },
      },

      filterOrdered: {
        deep: true,
        handler(v, old) {
          if (
            objectsUtils.equalsFirstLevel(v, old, ['ordering']) &&
            arraysUtils.equal(v.ordering, old.ordering)
          ) return;

          this.fetchMaterials();
        },
      },

      selected: {
        immediate: true,
        deep: true,
        handler(v) {
          if (!this.selectable) return;
          if (arraysUtils.equal(this.selectedV, v)) return;
          this.selectedV = v;
        },
      },

      selectedV: {
        deep: true,
        handler(v) {
          if (!this.selectable) return;
          this.$emit('update:selected', v);
        },
      },

      author: {
        immediate: true,
        handler(v) {
          this.authorV = v;
        },
      },

      license: {
        immediate: true,
        handler(v) {
          this.licenseV = v;
        }
      },

      editMode: {
        immediate: true,
        handler(v) {
          if (v && this.shownItem) this.openMaterialEditDialog();
        },
      },

      showEditDialog: {
        handler(v) {
          this.$emit('update:edit-mode', v);
        },
      },
    },

    created() {
      this.$bus.$emit('change:zoom-active', true);
      this.$bus.$on('create:material', this.onCreateMaterial);
      this.$bus.$on('delete:material', this.onDeletedMaterial);
      this.$bus.$on('change:material', this.onChangeMaterial);
      this.$bus.$on('material-dialog-show:switch-material', this.catchSwitchMaterialEmit);
      if (this.connectToGlobalSettings) {
        this.$bus.$on('items:refresh', this.onItemsRefresh);
      }
    },

    mounted() {
      this.$nextTick(() => {
        setTimeout(this.onResize, 0);
        // double to handle init animation
        setTimeout(this.onResize, 300);
        this.createResizeObserver().observe(this.$refs.container);
      })

    },

    beforeDestroy() {
      this.$bus.$emit('change:zoom-active', false);
      this.$bus.$off('create:material', this.onCreateMaterial);
      this.$bus.$off('delete:material', this.onDeletedMaterial);
      this.$bus.$off('change:material', this.onChangeMaterial);
      this.$bus.$off('material-dialog-show:switch-material', this.catchSwitchMaterialEmit);
      if (this.connectToGlobalSettings) {
        this.$bus.$off('items:refresh', this.onItemsRefresh);
      }
    },

    activated() {
      this.onResize();
      this.$bus.$emit('change:zoom-active', true);
    },

    deactivated() {
      this.$bus.$emit('change:zoom-active', false);
    },

    methods: {
      refresh() {
        this.updateKey++;
      },

      async onResize() {
        if (this.$nextTick) await this.$nextTick();
        let { clientWidth } = this.$refs.container;
        let { paddingLeft, paddingRight } = getComputedStyle(this.$refs.container);
        this.width = clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);
      },

      updateObserveEntryWidth(entries) {
        for (let entry of entries) {
          let widthTemp = entry.contentRect.width;
          this.widthTemp = widthTemp;
          const vm = this;
          setTimeout( () => {
            if (vm.widthTemp === widthTemp) vm.width = widthTemp;
          }, 50);
        }
      },

      createResizeObserver() {
        return new ResizeObserver(this.updateObserveEntryWidth);
      },

      onCreateMaterial() {
        this.fetchMaterials();
      },

      onChangeMaterial({ saved }) {
        this.setMaterial(saved);
      },

      onItemsRefresh() {
        this.refresh();
      },

      onSavedMaterial({ saved }) {
        this.showEditDialog = false;
        console.log({ onSavedMaterial: saved })
        // this.updateShownItem();
        this.setMaterial(saved)
      },

      onDeletedMaterial({ id }) {
        this.materialsTotal -= 1;
        this.removeMaterial(id);
      },

      onDeletedMaterialDialog({ id }) {
        this.showEditDialog = false;
        this.closeMaterialShowDialog();
        this.removeMaterial(id);
      },

      onClickTag(tag) {
        this.$emit('click:tag', tag);
        this.search = tag.title;
      },

      onClickAuthor(author) {
        this.$emit('click:author', author);
        this.$emit('update:author', author);
        this.authorV = author;
      },

      onClickLicense(license) {
        this.$emit('click:license', license);
        this.$emit('update:license', license);
        this.licenseV = license;
      },

      onShowDialogInput(isOpen) {
        if (!isOpen) this.shownItemIdV = undefined;
      },

      onEditDialogInput(isOpen) {
        if (!isOpen) {
          this.$refs.editDialog.reset();
          this.editedItem = {};
        }
      },

      onCreateMaterialDialog() {
        this.showDialogCreateMaterial = false;
      },

      openDialogCreateMaterial() {
        this.showDialogCreateMaterial = true;
      },

      openMaterialShowDialog(id) {
        this.shownItemIdV = id;
        this.setShownItemById(id).then( () => {
          if (this.editMode) this.openMaterialEditDialog();
        });
        this.showMaterial = true;
      },

      openMaterialEditDialog() {
        this.editedItem = { ...this.shownItem };
        this.showEditDialog = true;
      },

      closeMaterialShowDialog() {
        this.shownItemIdV = undefined;
        this.showMaterial = false;
      },

      removeMaterial(id) {
        let index = this.materials.findIndex( item => item.id === id );
        if (index >= 0) {
          this.materials.splice(index, 1);
        }
      },

      async fetchMaterials() {
        const { ignoreMaterialsIds: ignore } = this;
        this.loading = true;
        if (this.cancel) this.cancel();
        if (this.cancelNext) this.cancelNext();
        this.materials = [];

        return requestsUtils.fetchAllStart({
          getAll: this.$api.materials[this.getAllApiFunctionName],
          targetObject: this,
          mapPropertiesObject: {
            count: 'materialsTotal',
          },
          targetLoading: 'loading',
          targetCancel: 'cancel',
          targetNext: 'materialsNext',
          targetLoadingKey: 'loadingKey',
          filter: this.filterOrdered,
        })
        .then( ({ data: { results } }) => {
          this.materials = ignore?.length ? results.filter( ({ id }) => !ignore.includes(id) ) : results;
          this.$emit('fetched:materials');
        })
        .catch( () => { /* ignore */ })
      },

      async fetchMaterialsNext() {
        const { ignoreMaterialsIds: ignore } = this;
        return requestsUtils.fetchAllNext({
          getNext: this.$api.materials.getNext,
          targetObject: this,
          targetNext: 'materialsNext',
          targetLoading: 'loading',
          targetCancel: 'cancelNext',
          loadingKey: this.loadingKey,
          targetLoadingKey: 'loadingKey',
        })
        .then( ({ data: { results } }) => {
          this.materials.push(...(ignore?.length ? results.filter( ({ id }) => !ignore.includes(id) ) : results));
        })
        .catch( () => { /* ignore */ });
      },

      async fetchMaterial(id) {
        return new Promise( (resolve) => {
          this.$api.materials.getData(id)
          .then( resolve )
          .catch( () => {
            resolve(undefined);
          });
        });
      },

      async fetchShownItem() {
        return new Promise( (resolve) => {
          this.loadingMaterial = true;
          this.fetchMaterial(this.shownItemIdV)
          .then( result => {
            if (result.id !== this.shownItemIdV) return resolve();
            this.shownItem = result;
            resolve();
          })
          .finally( () => {
            this.loadingMaterial = false;
          });
        })
      },

      setMaterial(material) {
        let id = material?.id;
        if (!id) return;

        let index = this.materials.findIndex( item => item.id === id );
        if (index >= 0) this.$set(this.materials, index, material);
        if (this.shownItem?.id === material.id) this.shownItem = material;
        if (this.editedItem?.id === material.id) this.editedItem = material;
      },

      trackShownMaterial() {
        const { title, id } = this.shownItem;
        this.$gtag.event('view_item', {
          items: [{
            id: `${id}`,
            name: title
          }]
        });
      },

      async setShownItemById(id) {
        return new Promise( (resolve) => {
          let found = this.materials.find( item => item.id === id )
          if (found) {
            this.shownItem = found;
            this.shownItemIdV = found.id;
            this.setMaterialNeighbors();
            this.trackShownMaterial();
            return resolve();
          }
          this.fetchShownItem();
        });
      },

      setMaterialNeighbors() {
        const getNeighborMaterialId = (offset) => {
          try {
            return this.materials[this.materials.indexOf(this.shownItem) + offset].id;
          } catch (e) {
            return undefined;
          }
        }
        this.prevMaterialId = getNeighborMaterialId(-1);
        this.nextMaterialId = getNeighborMaterialId(+1);
      },

      async catchSwitchMaterialEmit(offset) {
        const { materials, shownItem } = this;
        // load more materials from backend if now shown material is penultimate
        if (materials[materials.length - 2]?.id === shownItem?.id) await this.fetchMaterialsNext();
        await this.setShownItemById((offset >= 0) ? this.nextMaterialId : this.prevMaterialId);
      },
    }
  }
</script>